'use client';

import { Box, Text, Tooltip } from '@radix-ui/themes';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { DateTime } from 'luxon';
import { useState, useCallback } from 'react';
import React from 'react';
import { useRentalCalendar } from '@trashlab/viteapp/src/components/shop-v3/hooks/useRentalCalendar';
import type { SitesShopCalendarProps } from '@trashlab/sitesapp/src/components/shop/calendar-forecast-input';

export const ShopCalendar = ({
  tenantId,
  compact = false,
  value,
  onChange,
  daysLimit,
  onDaysOverChange,
  pickupDateOptional = false,
  forecastRequirement,
  startRentalFromDeliveryDate = false,
  blockSameDayOrder = false,
  sameDayOrderCutoffTime,
  blockedDaysFromToday = 0,
  countInactiveDaysAsRentalDays = false,
}: SitesShopCalendarProps) => {
  const blockDatesWithoutInventory =
    forecastRequirement.blockDatesWithoutInventory ?? false;
  const {
    isLoading,
    handleDateClick,
    specialDates,
    businessHours,
    forecast,
    isDateUnavailable,
    isInRange,
    isOverLimit,
    isFirstInRange,
    isLastInRange,
    getDateInfo,
    setHoveredDate,
  } = useRentalCalendar({
    tenantId,
    value,
    onChange,
    daysLimit,
    onDaysOverChange,
    pickupDateOptional,
    forecastRequirement,
    startRentalFromDeliveryDate,
    blockSameDayOrder,
    sameDayOrderCutoffTime,
    blockedDaysFromToday,
    countInactiveDaysAsRentalDays,
  });

  const [currentMonth, setCurrentMonth] = useState(() => {
    if (value?.[0]) {
      return DateTime.fromJSDate(value[0]).startOf('month').toJSDate();
    }
    return DateTime.now().startOf('month').toJSDate();
  });

  const handlePrevMonth = () => {
    setCurrentMonth(
      DateTime.fromJSDate(currentMonth).minus({ months: 1 }).toJSDate(),
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      DateTime.fromJSDate(currentMonth).plus({ months: 1 }).toJSDate(),
    );
  };

  const generateCalendarDays = useCallback(() => {
    const dt = DateTime.fromJSDate(currentMonth);
    const year = dt.year;
    const month = dt.month;

    const firstDay = dt.startOf('month');
    const lastDay = dt.endOf('month');

    const firstDayOfWeek = firstDay.weekday === 7 ? 0 : firstDay.weekday;

    const daysInMonth = lastDay.day;

    const days: Array<{ date: Date; isCurrentMonth: boolean }> = [];

    const prevMonth = dt.minus({ months: 1 });
    const prevMonthLastDay = prevMonth.endOf('month').day;

    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const dayOffset = prevMonthLastDay - i;
      days.push({
        date: DateTime.local(
          prevMonth.year,
          prevMonth.month,
          dayOffset,
        ).toJSDate(),
        isCurrentMonth: false,
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: DateTime.local(year, month, i).toJSDate(),
        isCurrentMonth: true,
      });
    }

    const nextMonth = dt.plus({ months: 1 });
    const remainingDays = 42 - days.length;

    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: DateTime.local(nextMonth.year, nextMonth.month, i).toJSDate(),
        isCurrentMonth: false,
      });
    }

    return days;
  }, [currentMonth]);

  const calendarDays = generateCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (isLoading) {
    return (
      <Box className="flex justify-center p-4">
        <Text>Loading calendar...</Text>
      </Box>
    );
  }

  return (
    <Box className="w-full">
      <Box
        className="w-full max-w-full bg-white rounded-md"
        id="service-dates-calendar"
      >
        <Box className="flex justify-between items-center p-2.5 mb-2.5">
          <Text className="font-bold text-lg">
            {DateTime.fromJSDate(currentMonth).toFormat('MMMM yyyy')}
          </Text>
          <Box className="flex gap-1.5">
            <button
              className="bg-[var(--accent-3)] hover:bg-[var(--accent-4)] w-8 h-8 rounded flex items-center justify-center cursor-pointer border-none"
              onClick={handlePrevMonth}
            >
              <IconChevronLeft size={18} />
            </button>
            <button
              className="bg-[var(--accent-3)] hover:bg-[var(--accent-4)] w-8 h-8 rounded flex items-center justify-center cursor-pointer border-none"
              onClick={handleNextMonth}
            >
              <IconChevronRight size={18} />
            </button>
          </Box>
        </Box>

        <Box className="grid grid-cols-7 text-center font-bold uppercase text-xs mb-1.5 py-1.5">
          {weekDays.map((day) => (
            <Box key={day}>{day}</Box>
          ))}
        </Box>

        <Box className="grid grid-cols-7 auto-rows-[40px] gap-0.5">
          {calendarDays.map(({ date, isCurrentMonth }) => {
            const isDisabled = isDateUnavailable(date);
            const inRange = isInRange(date);
            const isFirst = isFirstInRange(date);
            const isLast = isLastInRange(date);
            const isOver = isOverLimit(date);
            const isToday = DateTime.fromJSDate(date).hasSame(
              DateTime.now(),
              'day',
            );

            const { specialDate, businessHour, hasInventory } =
              getDateInfo(date);

            const tooltipContent = isDisabled
              ? specialDate?.reason ||
                (businessHour?.status === 'CLOSED'
                  ? 'Business closed'
                  : blockDatesWithoutInventory && !hasInventory
                  ? 'No inventory'
                  : 'Unavailable')
              : '';

            let dayClasses = [
              'relative flex justify-center items-center rounded cursor-pointer select-none',
            ];

            if (!isCurrentMonth) {
              dayClasses.push('text-gray-500');
            }

            if (isDisabled) {
              dayClasses = [
                'relative flex justify-center items-center rounded line-through text-gray-500 bg-gray-100 cursor-not-allowed select-none',
              ];
            } else if (inRange && !isFirst && !isLast) {
              if (isOver && !isDateUnavailable(date) && !isDisabled) {
                dayClasses.push('bg-orange-200');
              } else if (isDateUnavailable(date) || isDisabled) {
                dayClasses.push('bg-gray-100');
              } else {
                dayClasses.push('bg-[var(--accent-5)]');
              }
            } else if (isFirst) {
              dayClasses.push('bg-[var(--accent-9)] text-white font-bold');
            } else if (isLast) {
              if (isOver && !isDateUnavailable(date) && !isDisabled) {
                dayClasses.push('bg-orange-400 text-white font-bold');
              } else {
                dayClasses.push('bg-[var(--accent-9)] text-white font-bold');
              }
            } else if (isOver && !isDateUnavailable(date) && !isDisabled) {
              dayClasses.push(
                'bg-orange-200 hover:bg-orange-400 hover:text-white',
              );
            } else {
              dayClasses.push('hover:bg-[var(--accent-3)]');
            }

            if (isToday) {
              dayClasses.push('border border-[var(--accent-6)]');
            }

            const renderDay = (
              <Box
                className={dayClasses.join(' ')}
                onClick={() => !isDisabled && handleDateClick(date)}
                onMouseOver={() => {
                  if (!isDisabled && value?.[0] && !value?.[1]) {
                    setHoveredDate(date);
                  }
                }}
                data-is-over={isOver ? 'true' : 'false'}
              >
                {date.getDate()}
              </Box>
            );

            return (
              <React.Fragment key={date.toISOString()}>
                {isDisabled && tooltipContent ? (
                  <Tooltip content={tooltipContent} delayDuration={300}>
                    {renderDay}
                  </Tooltip>
                ) : (
                  renderDay
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default ShopCalendar;
